import { useMemo } from 'react'

// Read-only reconstruction of what the student's Live Preview pane showed at this point in
// the replay - previously the replay tool had no rendered-HTML view at all for GUI sessions,
// only the raw HTML/CSS/JS source (see ReplayCodeView.jsx). Deliberately not the full
// LivePreview.jsx: that component also wires up two-way postMessage instrumentation to
// capture click/console/scroll telemetry from the iframe, none of which applies here since
// this is a static snapshot of past code, not a live session collecting new telemetry - a
// plain sandboxed iframe is enough to show what the page looked like.
export default function ReplayLivePreview({ code = '' }) {
  const { html, css, js } = useMemo(() => {
    try {
      const parsed = JSON.parse(code)
      return {
        html: parsed?.html || '',
        css: parsed?.css || '',
        js: parsed?.js || '',
      }
    } catch (e) {
      return { html: '', css: '', js: '' }
    }
  }, [code])

  const srcDoc = useMemo(() => `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src https: data:; form-action 'none';">
      <style>
        html, body { margin: 0; padding: 16px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background-color: #ffffff; color: #171717; }
        ${css}
      </style>
    </head>
    <body>
      ${html}
      <script>${js}</script>
    </body>
    </html>
  `, [html, css, js])

  if (!html && !css && !js) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: '24px',
      }}>
        No code recorded yet at this point in the replay.
      </div>
    )
  }

  return (
    <iframe
      title="Replay Live Preview"
      srcDoc={srcDoc}
      sandbox="allow-scripts"
      style={{ width: '100%', height: '100%', border: 'none', background: '#ffffff' }}
    />
  )
}
